import React from 'react'
import {
  DiagramOption,
  ResponseDiagramType,
  getSlsDiagramOptionsForMaterial,
  strainStressDiagramOptions,
  ulsDiagramOptions
} from './types'
import { useTranslation } from '@structura/i18n'
import CombinationFactorDetails from './CombinationFactorDetails'

type TFunc = ReturnType<typeof useTranslation>['t']

interface ResponseControlPanelProps
{
  result: any
  onCalculateResponse: () => Promise<void>
  calculatingResponse: boolean
  selectedDiagram: ResponseDiagramType
  setSelectedDiagram: (diagram: ResponseDiagramType) => void
  showValues: boolean
  setShowValues: (value: boolean) => void
  showMaxMin: boolean
  setShowMaxMin: (value: boolean) => void
  valueDecimals: 2 | 4
  setValueDecimals: (value: 2 | 4) => void
  diagramScale: number
  setDiagramScale: (value: number) => void
  valueDensity: number
  setValueDensity: (value: number) => void
  valueFontSize: number
  setValueFontSize: (value: number) => void
  selectedUlsCombination: string
  onSelectUlsCombination: (value: string) => void
  selectedSlsCombination: string
  onSelectSlsCombination: (value: string) => void
  ulsCombinations: Array<{ id: string; label: string }>
  slsCombinations: Array<{ id: string; label: string }>
  activeCombinationLabel: string
  onToggleUlsDetails: () => void
  onToggleSlsDetails: () => void
  ulsCombinationDetails: any | null
  slsCombinationDetails: any | null
  // True when some SLS combination in the current response evaluated the shared steel/reinforcement
  // stress check — gates the concrete and timber stress cards.
  steelStressEvaluated: boolean
  currentMaterial: string
}

function renderDiagramCards(
  options: DiagramOption[],
  selectedDiagram: ResponseDiagramType,
  setSelectedDiagram: (diagram: ResponseDiagramType) => void,
  t: TFunc
): React.ReactNode
{
  return (
    <div className="row g-2">
      {options.map((option) => {
        const title = option.titleKey ? t(option.titleKey) : option.title
        const description = option.descKey ? t(option.descKey) : option.description
        return (
        <div key={option.id} className="col-6">
          <div
            className={`card h-100 cursor-pointer ${selectedDiagram === option.id ? 'border-primary bg-light' : 'border-secondary'}`}
            onClick={() => setSelectedDiagram(option.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body text-center py-2">
              <div className={`fw-bold small ${selectedDiagram === option.id ? 'text-primary' : 'text-muted'}`}>
                {title}
              </div>
              {description ? (
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {description}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        )
      })}
    </div>
  )
}

const ResponseControlPanel: React.FC<ResponseControlPanelProps> = ({
  result,
  onCalculateResponse,
  calculatingResponse,
  selectedDiagram,
  setSelectedDiagram,
  showValues,
  setShowValues,
  showMaxMin,
  setShowMaxMin,
  valueDecimals,
  setValueDecimals,
  diagramScale,
  setDiagramScale,
  valueDensity,
  setValueDensity,
  valueFontSize,
  setValueFontSize,
  selectedUlsCombination,
  onSelectUlsCombination,
  selectedSlsCombination,
  onSelectSlsCombination,
  ulsCombinations,
  slsCombinations,
  activeCombinationLabel,
  onToggleUlsDetails,
  onToggleSlsDetails,
  ulsCombinationDetails,
  slsCombinationDetails,
  steelStressEvaluated,
  currentMaterial
}) => {
  const { t } = useTranslation()
  // Get material-specific SLS options
  const filteredSlsOptions = getSlsDiagramOptionsForMaterial(currentMaterial, steelStressEvaluated)

  return (
  <div>
    <div className="mb-4">
      <div className="d-grid">
        <button
          className="btn btn-success btn-lg"
          onClick={onCalculateResponse}
          disabled={!(result?.Success || result?.success) || calculatingResponse}
        >
          {calculatingResponse ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              {t('beam.response.calcRunning')}
            </>
          ) : (
            <>
              <i className="bi bi-calculator me-2"></i>
              {t('beam.response.calc')}
            </>
          )}
        </button>
        {result && (result.Success || result.success) && !result.ResponseData && (
          <small className="text-muted mt-2">
            <i className="bi bi-info-circle me-1"></i>
            {t('beam.response.calcHint')}
          </small>
        )}
        {result?.ResponseData && (
          <small className="text-success mt-2">
            <i className="bi bi-check-circle me-1"></i>
            {t('beam.response.dataLoaded')}
          </small>
        )}
      </div>
    </div>

    <hr />

    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-bold mb-0">{t('beam.response.urUls')}</label>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onToggleUlsDetails}
          disabled={ulsCombinations.length === 0}
        >
          {t('beam.response.showDetailsCombo')}
        </button>
      </div>
      {ulsCombinationDetails && <CombinationFactorDetails combination={ulsCombinationDetails} />}
      <div className="mb-2">
        <select
          className="form-select form-select-sm"
          value={selectedUlsCombination}
          onChange={(e) => onSelectUlsCombination(e.target.value)}
          onClick={() => {
            // Ensure ULS becomes the active combination even when there's only one option
            if (ulsCombinations.length > 0) {
              const targetId = selectedUlsCombination || ulsCombinations[0].id
              if (targetId) onSelectUlsCombination(targetId)
            }
          }}
          disabled={ulsCombinations.length === 0}
        >
          {ulsCombinations.length === 0 && <option value="">{t('beam.response.noUlsCombos')}</option>}
          {ulsCombinations.map((combo) => (
            <option key={combo.id} value={combo.id}>
              {combo.label}
            </option>
          ))}
        </select>
      </div>
      {renderDiagramCards(ulsDiagramOptions, selectedDiagram, setSelectedDiagram, t)}
    </div>

    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-bold mb-0">{t('beam.response.urSls')}</label>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onToggleSlsDetails}
          disabled={slsCombinations.length === 0}
        >
          {t('beam.response.showDetailsCombo')}
        </button>
      </div>
      {slsCombinationDetails && <CombinationFactorDetails combination={slsCombinationDetails} />}
      <div className="mb-2">
        <select
          className="form-select form-select-sm"
          value={selectedSlsCombination}
          onChange={(e) => onSelectSlsCombination(e.target.value)}
          onClick={() => {
            // Ensure SLS becomes the active combination even when there's only one option
            if (slsCombinations.length > 0) {
              const targetId = selectedSlsCombination || slsCombinations[0].id
              if (targetId) onSelectSlsCombination(targetId)
            }
          }}
          disabled={slsCombinations.length === 0}
        >
          {slsCombinations.length === 0 && <option value="">{t('beam.response.noSlsCombos')}</option>}
          {slsCombinations.map((combo) => (
            <option key={combo.id} value={combo.id}>
              {combo.label}
            </option>
          ))}
        </select>
      </div>
      {renderDiagramCards(filteredSlsOptions, selectedDiagram, setSelectedDiagram, t)}
    </div>

    <div className="mb-4">
      <label className="form-label fw-bold">{t('beam.results.displayOptions')}</label>
      <div className="row">
        <div className="col-6">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={showValues}
              onChange={(event) => setShowValues(event.target.checked)}
              id="respShowValues"
            />
            <label className="form-check-label" htmlFor="respShowValues">
              {t('beam.results.showValues')}
            </label>
          </div>
        </div>
        <div className="col-6">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={showMaxMin}
              onChange={(event) => setShowMaxMin(event.target.checked)}
              id="respShowMaxMin"
            />
            <label className="form-check-label" htmlFor="respShowMaxMin">
              {t('beam.results.highlightMaxMin')}
            </label>
          </div>
        </div>
        <div className="col-12 mt-2">
          <label className="form-label">{t('beam.results.valuePrecision')}</label>
          <div className="btn-group btn-group-sm" role="group">
            <button
              className={`btn btn-outline-secondary ${valueDecimals === 2 ? 'active' : ''}`}
              onClick={() => setValueDecimals(2)}
              type="button"
            >
              {t('beam.results.dp2')}
            </button>
            <button
              className={`btn btn-outline-secondary ${valueDecimals === 4 ? 'active' : ''}`}
              onClick={() => setValueDecimals(4)}
              type="button"
            >
              {t('beam.results.dp4')}
            </button>
          </div>
        </div>
        <div className="col-12 mt-2">
          <label className="form-label">{t('beam.results.fontSize')}</label>
          <div className="btn-group btn-group-sm" role="group">
            {[8, 10, 12, 14, 16, 18].map(size => (
              <button
                key={size}
                className={`btn btn-outline-secondary ${valueFontSize === size ? 'active' : ''}`}
                onClick={() => setValueFontSize(size)}
                type="button"
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>

    <div className="mb-4">
      <label className="form-label fw-bold">{t('beam.results.diagramScale')}</label>
      <div className="row align-items-center">
        <div className="col-8">
          <input
            type="range"
            className="form-range"
            min="0.1"
            max="3.0"
            step="0.1"
            value={diagramScale}
            onChange={(event) => setDiagramScale(parseFloat(event.target.value))}
          />
        </div>
        <div className="col-4">
          <span className="badge bg-secondary">{diagramScale.toFixed(1)}x</span>
        </div>
      </div>
    </div>

    <div className="mb-4">
      <label className="form-label fw-bold">{t('beam.results.valueDensity')}</label>
      <div className="row align-items-center">
        <div className="col-8">
          <input
            type="range"
            className="form-range"
            min="1"
            max="20"
            step="1"
            value={valueDensity}
            onChange={(event) => setValueDensity(parseInt(event.target.value, 10))}
          />
        </div>
        <div className="col-4">
          <span className="beam-badge">
            {t('beam.results.everyNPts', { n: valueDensity })}
          </span>
        </div>
      </div>
      <small className="text-muted">
        {t('beam.results.valueDensityHint')}
      </small>
    </div>

    <hr />

    <div className="mb-4">
      <label className="form-label fw-bold">
        {t('beam.response.strainsStresses')}
        {activeCombinationLabel && (
          <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.85rem' }}>
            ({activeCombinationLabel})
          </span>
        )}
      </label>
      <div className="alert alert-info py-2 small mb-2">
        <i className="bi bi-info-circle me-1"></i>
        {t('beam.response.strainStressInfo')}
      </div>
      <div className="row g-0 mb-1">
        <div className="col-6 text-center"><small className="text-muted fw-semibold">{t('beam.response.stresses')}</small></div>
        <div className="col-6 text-center"><small className="text-muted fw-semibold">{t('beam.response.strains')}</small></div>
      </div>
      {renderDiagramCards(strainStressDiagramOptions, selectedDiagram, setSelectedDiagram, t)}
    </div>
  </div>
  )
}

export default ResponseControlPanel


